import { z } from 'zod';
import { ApplySkipReasonSchema, ApplySkippedFieldSchema } from './document-analysis.schemas';
import { refersTo } from '../common/refers-to';
import { FACT_MODALITIES } from './fact-provenance.schemas';

// Property-level promotion suggestions (IMM-500.4) — the facts layer's offers onto
// the Objekt's own fields. Where the per-document apply reviews one document at a
// time, this raises promotion to the whole property: one entry per Objekt field the
// facts layer can fill, each carrying its value, provenance and confidence, and the
// current Objekt value it would overwrite. A key whose live facts disagree is
// offered no value — the layer presents the conflict and the member resolves it
// (parent Resolved Decision 4). No value is ever written without the member's
// confirmation.

// One reading behind a key, with its own provenance and confidence: a live document
// fact, or a field reading from an e-mail, call or note the caller may see
// (IMM-1050.6). Every supporting reading is listed whether or not one was suggested,
// so a member can see the disagreement behind a conflicted key.
export const PropertyFactSuggestionCandidateSchema = z.object({
  value: z.unknown(),
  sourceId: z.string(),
  // What `sourceId` identifies (IMM-1050.6): a document for DOCUMENT (and the
  // reserved IMAGE), an activity record for EMAIL, CALL and NOTE. NONE is a fact
  // not read from a medium.
  sourceType: z.enum([...FACT_MODALITIES, 'EMAIL', 'CALL', 'NOTE']),
  sourceCategory: z.string().nullable(),
  confidence: z.number(),
});

export type PropertyFactSuggestionCandidate = z.infer<typeof PropertyFactSuggestionCandidateSchema>;

// The value the layer offers for a key: the highest-confidence supporting reading
// and its confidence. Null when the facts disagree or none clears the confidence
// floor — the candidates are still returned for the member to judge.
export const PropertyFactSuggestedValueSchema = z.object({
  value: z.unknown(),
  confidence: z.number(),
});

// Why `apply` could not write a suggestion onto the Objekt as it stands (IMM-1594), judged
// as one apply of every value the list offers, with the apply's own coercion and section
// rules: `section-incomplete` (an all-or-nothing section the Objekt does not hold yet),
// `section-not-applicable` (a rent on a sale, a price on a rental), `certificate-not-gmodg`,
// `energy-class-off-scale`, `invalid-value` (not a value the field can hold) and
// `unsupported-field` (the field is not writable).
export const PropertyFactSuggestionBlockedReasonSchema = ApplySkipReasonSchema.extract([
  'unsupported-field',
  'invalid-value',
  'section-incomplete',
  'section-not-applicable',
  'certificate-not-gmodg',
  'energy-class-off-scale',
]);

export type PropertyFactSuggestionBlockedReason = z.infer<typeof PropertyFactSuggestionBlockedReasonSchema>;

// One suggestion: an Objekt field the facts layer can fill. `suggested` is null
// when the facts disagree (`conflict: true`), none clears the floor, or the value
// cannot be written onto the Objekt as it stands (`blockedReason`); `overwrites`
// carries the Objekt's current value when it differs from the suggestion, so the
// member confirms an overwrite knowingly, and is null when the field is empty.
export const PropertyFactSuggestionSchema = z.object({
  factKey: z.string(),
  propertyFieldKey: z.string(),
  label: z.string(),
  suggested: PropertyFactSuggestedValueSchema.nullable(),
  conflict: z.boolean(),
  blockedReason: PropertyFactSuggestionBlockedReasonSchema.nullable(),
  candidates: z.array(PropertyFactSuggestionCandidateSchema),
  overwrites: z.object({ value: z.unknown() }).nullable(),
});

export type PropertyFactSuggestion = z.infer<typeof PropertyFactSuggestionSchema>;

export const PropertyFactSuggestionsSchema = z.object({
  propertyId: refersTo('property', z.string()),
  suggestions: z.array(PropertyFactSuggestionSchema),
});

export type PropertyFactSuggestions = z.infer<typeof PropertyFactSuggestionsSchema>;

// Body for applying suggestions (IMM-500.4): exactly the keys the member confirmed.
// Only these are written — nothing else — and each is re-derived server-side, so a
// key the layer would not offer right now (conflicting, below the floor, unmapped)
// is refused even if a stale client sends it. `factKeys` is de-duplicated so a
// repeated key cannot double-count, and bounded so the body cannot smuggle an
// unbounded list. The cap comfortably exceeds every mappable Objekt field.
export const ApplyFactSuggestionsBodySchema = z
  .object({
    factKeys: z
      .array(z.string().min(1))
      .min(1)
      .max(64)
      .transform((keys) => [...new Set(keys)]),
  })
  .strict();

export type ApplyFactSuggestionsBody = z.infer<typeof ApplyFactSuggestionsBodySchema>;

// The outcome of one apply: which keys reached the Objekt and which did not (with
// the reason, reusing the per-document `ApplySkippedField` shape). Partial success
// is normal and not an error — the caller reports it. The updated Objekt itself is
// not echoed; the client re-reads it.
export const ApplyFactSuggestionsResponseSchema = z.object({
  appliedFieldKeys: z.array(z.string()),
  skippedFields: z.array(ApplySkippedFieldSchema),
});

export type ApplyFactSuggestionsResponse = z.infer<typeof ApplyFactSuggestionsResponseSchema>;
